import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Printer } from "lucide-react";

// Terms of Trade for Carriage of Goods and Livestock — MV William Fraser.
// Wording is verbatim from "McCallum Bros Terms of Trade - 12 August 2026 [FWM]".
// If the legal text changes, update TERMS below AND the TERMS_VERSION in Apply.jsx.
export const TERMS_VERSION = "2026-08-12";
const TERMS_DATE = "12 August 2026";

const TERMS = [
 {
  "text": "These Terms of Trade for Carriage of Goods and Livestock are supplied by McCallum Bros Limited (NZBN 9429040749303) to the Customer on the following terms and conditions.",
  "t": "intro"
 },
 {
  "n": "1",
  "title": "Parties, Definitions and Application",
  "t": "heading",
  "text": ""
 },
 {
  "n": "1.1",
  "text": "In these Terms, unless the context requires otherwise:",
  "t": "clause"
 },
 {
  "text": "\"Agent\" means any stock agent, freight forwarder, or other representative acting on behalf of an Owner or Consignor in connection with a Booking.",
  "t": "def"
 },
 {
  "text": "“Applicable Authority” means any government or any public, statutory, governmental, semi-governmental, local governmental or judicial body, entity or authority anywhere in the world, including a Minister of the Crown (in any right), port authority or other regulatory authority.",
  "t": "def"
 },
 {
  "text": "\"FAF\" means the fuel adjustment factor applied in addition to the applicable freight rates charged by the Carrier as set out on its website and booking system.",
  "t": "def"
 },
 {
  "text": "\"Booking\" means a request for carriage accepted by the Carrier in accordance with clause 2.",
  "t": "def"
 },
 {
  "text": "“Business Day” means a day (other than a Saturday, Sunday or public holiday) on which registered banks are generally open for business in Auckland, New Zealand.",
  "t": "def"
 },
 {
  "text": "“Booking and Declaration Form” means the booking and declaration form to be completed by the Customer as provided by the Carrier.",
  "t": "def"
 },
 {
  "text": "\"Carrier\" means McCallum Bros Limited (NZBN 9429040749303), and includes its subsidiaries, shareholders, officers, employees and agents and any actual carrier engaged by it.",
  "t": "def"
 },
 {
  "text": "\"CCLA\" means the Contract and Commercial Law Act 2017, which may be amended from time to time.",
  "t": "def"
 },
 {
  "text": "“Claim” includes a claim, notice, demand, action, proceeding, litigation, investigation, judgment or entitlement to payment or compensation, however arising, whether present, unascertained, immediate, future or contingent, whether based in tort, contract, statute or otherwise and whether involving a third party or a party to these Terms.",
  "t": "def"
 },
 {
  "text": "“Consequential Loss” means any Loss that is indirect, consequential or special in nature, including, without limitation any:",
  "t": "def"
 },
 {
  "label": "a)",
  "text": "loss of profit;",
  "t": "defitem"
 },
 {
  "label": "b)",
  "text": "loss of revenue;",
  "t": "defitem"
 },
 {
  "label": "c)",
  "text": "loss of production;",
  "t": "defitem"
 },
 {
  "label": "d)",
  "text": "loss of use;",
  "t": "defitem"
 },
 {
  "label": "e)",
  "text": "loss of business or business opportunity;",
  "t": "defitem"
 },
 {
  "label": "f)",
  "text": "loss of contract (other than these Terms);",
  "t": "defitem"
 },
 {
  "label": "g)",
  "text": "loss of anticipated savings;",
  "t": "defitem"
 },
 {
  "label": "h)",
  "text": "loss of goodwill;",
  "t": "defitem"
 },
 {
  "label": "i)",
  "text": "damage to reputation; or",
  "t": "defitem"
 },
 {
  "label": "j)",
  "text": "loss of market,",
  "t": "defitem"
 },
 {
  "label": "k)",
  "text": "loss of breeding value, genetic value or reproductive potential of any Livestock;",
  "t": "defitem"
 },
 {
  "label": "l)",
  "text": "loss arising from reduced carcass quality, carcass grading, meat yield or processing value;",
  "t": "defitem"
 },
 {
  "label": "m)",
  "text": "loss arising from missed processing, slaughter or delivery windows (if applicable);",
  "t": "defitem"
 },
 {
  "label": "n)",
  "text": "loss arising from animal stress, weight loss or reduced condition following carriage, except to the extent such Loss cannot lawfully be excluded; and",
  "t": "defitem"
 },
 {
  "label": "o)",
  "text": "any similar economic or financial Loss,",
  "t": "defitem"
 },
 {
  "text": "whether arising in contract, tort (including negligence), equity, under statute or otherwise.",
  "t": "deftrailer"
 },
 {
  "text": "\"Consignor\" means the person or entity who delivers Goods or Livestock to the Carrier for carriage, whether or not that person or entity is also the Owner.",
  "t": "def"
 },
 {
  "text": "\"Customer\" means the Consignor, Owner, and/or Agent, jointly and severally, who has entered into these Terms with the Carrier.",
  "t": "def"
 },
 {
  "text": "\"Dangerous Goods\" means any substance, material, or article classified as dangerous goods under the IMDG Code, the Land Transport Rule: Dangerous Goods 2005, Maritime Rules Part 24A or any other applicable Laws.",
  "t": "def"
 },
 {
  "text": "\"Goods\" includes all freight, cargo, machinery, vehicles, and equipment carried by the Carrier, but does not include Livestock unless the context requires otherwise.",
  "t": "def"
 },
 {
  "text": "“Law(s)” means the common law and any statute, regulation, order, rule, subordinate legislation, or other document enforceable under any statute, regulation, order, rule or subordinate legislation.",
  "t": "def"
 },
 {
  "text": "“Loss” includes, but is not limited to, all losses (including Consequential Loss), liabilities, damages, fines, penalties, interest, awards and amounts payable, costs and expenses (including legal and other professional costs and expenses, in each case on a full indemnity basis).",
  "t": "def"
 },
 {
  "text": "\"Livestock\" means cattle, sheep, or other animals accepted by the Carrier for carriage.",
  "t": "def"
 },
 {
  "text": "“Livestock Handling Procedures” means the Carrier’s Livestock handling procedures published on its website and may be amended from time to time.",
  "t": "def"
 },
 {
  "text": "“Master” means the master of the Vessel.",
  "t": "def"
 },
 {
  "text": "\"Owner\" means the person or entity that owns the Goods or Livestock at the time of carriage, including the Owner’s Agent(s).",
  "t": "def"
 },
 {
  "text": "“Personnel” means, in relation to a party, the officers, employees, Master, Stockperson, crew, contractors (including employees of such contractors) and agents of that party.",
  "t": "def"
 },
 {
  "text": "\"Port\" means Waitangi or Pitt Island (Chatham Islands), Napier or Timaru (mainland New Zealand) or any other port serviced by the Carrier from time to time.",
  "t": "def"
 },
 {
  "text": "“PPSA” means the Personal Properties Securities Act 1999.",
  "t": "def"
 },
 {
  "text": "“PPSR” means the personal properties securities register.",
  "t": "def"
 },
 {
  "text": "“Sailing Schedule” means the sailing schedule provided by the Carrier on its website and booking system which sets out the Vessel’s sailing schedule, Port(s) of call, loading and unloading dates and any other applicable information relating to the Voyage.",
  "t": "def"
 },
 {
  "text": "\"Shipping Service\" means the carriage of fuel, Livestock, machinery and general Goods between the Chatham Islands and mainland New Zealand ports provided by the Carrier using the Vessel.",
  "t": "def"
 },
 {
  "text": "“Stockperson” means the stockperson aboard the Vessel.",
  "t": "def"
 },
 {
  "text": "“Terms” means these terms of trade which may be amended from time to time with no prior notice.",
  "t": "def"
 },
 {
  "text": "\"Unit of Goods\" has the meaning given in section 247 of the CCLA.",
  "t": "def"
 },
 {
  "text": "\"Vessel\" means the MV William Fraser, or any other vessel operated or chartered by the Carrier in providing the Shipping Service.",
  "t": "def"
 },
 {
  "text": "\"Voyage\" means a single sailing of the Vessel between Ports.",
  "t": "def"
 },
 {
  "n": "1.2",
  "text": "These Terms apply to every contract of carriage of Goods, Livestock, and Dangerous Goods entered into by the Carrier for voyages between the Chatham and Pitt Islands and mainland New Zealand, and prevail over any conflicting terms proposed by the Customer, whether in a purchase order, Booking form, or otherwise, unless expressly agreed in writing by the Carrier.",
  "t": "clause"
 },
 {
  "n": "1.3",
  "text": "These Terms are separate from, and do not affect, any general terms of trade published by the Carrier or its related companies for the supply of goods and services unrelated to carriage (for example, aggregate, concrete, and equestrian surfacing products).",
  "t": "clause"
 },
 {
  "n": "1.4",
  "text": "Nothing in these Terms limits the Carrier's rights or obligations under the CCLA, the Maritime Transport Act 1994, the Animal Welfare Act 1999, or any other applicable Laws, except to the extent contracting out is expressly permitted by Law and is recorded in these Terms.",
  "t": "clause"
 },
 {
  "n": "2",
  "title": "Booking",
  "t": "heading",
  "text": ""
 },
 {
  "n": "2.1",
  "text": "All Livestock and Goods must be booked with the Carrier in advance using the Carrier’s online booking portal or other method agreed in writing from the Carrier.",
  "t": "clause"
 },
 {
  "n": "2.2",
  "text": "For each consignment of Livestock, the Consignor must complete and submit the Carrier's Booking & Declaration Form through the Carrier’s online booking portal (or other agreed method) no later than the date set out in the commercial invoice or two days before the commencement of loading of the Vessel, whichever occurs first, in accordance with the Sailing Schedule.",
  "t": "clause"
 },
 {
  "n": "2.3",
  "text": "The Carrier or the Master may refuse to load any Livestock that do not meet the fitness criteria in the Carrier's Livestock Handling Procedures, or any Goods that are not properly manifested, packaged, or declared, without liability to the Carrier.",
  "t": "clause"
 },
 {
  "n": "2.4",
  "text": "The Carrier is not obliged to issue any bill of lading, sea waybill or other transport document in respect of any consignment. The Carrier may, at its discretion and on the Customer’s request, issue a non-negotiable sea waybill or receipt for a consignment. Any such document is issued as evidence of receipt of the Goods or Livestock only, does not constitute a document of title, is not negotiable or transferable, and is in all respects subject to and incorporates these Terms. In the event of any inconsistency between these Terms and any such document, these Terms prevail.",
  "t": "clause"
 },
 {
  "n": "3",
  "title": "Freight Rates and Charges",
  "t": "heading",
  "text": ""
 },
 {
  "n": "3.1",
  "text": "Freight rates and charges are payable, exclusive of GST and FAF with the current freight rates made available by the Carrier through its website and booking system.",
  "t": "clause"
 },
 {
  "n": "3.2",
  "text": "Rates exclude a FAF, which the Carrier will disclose and calculate transparently on its website.",
  "t": "clause"
 },
 {
  "n": "3.3",
  "text": "The inter-island charge payable by the Carrier to the relevant Applicable Authority applies only to Livestock or Goods moving directly between Chatham Island and Pitt Island, and does not apply to carriage between either island and a mainland New Zealand Port.",
  "t": "clause"
 },
 {
  "n": "3.4",
  "text": "The Carrier may vary its rates on reasonable notice to reflect increases in Port charges, regulatory costs, or other costs beyond its control. Variations will be notified to the Customer in writing and will not apply retrospectively to Bookings already accepted.",
  "t": "clause"
 },
 {
  "n": "3.5",
  "text": "The Customer shall be liable for, and agrees to pay, the expenses reasonably incurred by the Carrier as a result of:",
  "t": "clause"
 },
 {
  "label": "(a)",
  "text": "the Goods or Livestock not being made promptly available to the Carrier or its Personnel receiving the same, at the place of dispatch specified; or",
  "t": "item"
 },
 {
  "label": "(b)",
  "text": "the Customer or its Agent failing to accept delivery of the Goods or Livestock immediately on its arrival at the place at which it is to be delivered.",
  "t": "item"
 },
 {
  "n": "4",
  "title": "Payment Terms and Credit",
  "t": "heading",
  "text": ""
 },
 {
  "n": "4.1",
  "text": "All payments must be made in full, without deduction or set-off, by the due date stated on the Carrier's invoice. For the avoidance of doubt, unless otherwise agreed in writing by the Carrier, all freight rates and charges for Goods and/or Livestock must be made in full by the Customer prior to loading on board the Vessel.",
  "t": "clause"
 },
 {
  "n": "4.2",
  "text": "If payment is not made by the due date, the Carrier may charge interest on the overdue balance at a rate of 2.5% per calendar month above the rate charged by the Carrier's bank, calculated from the due date until the date of actual payment.",
  "t": "clause"
 },
 {
  "n": "4.3",
  "text": "The Carrier may, on 5 days' written notice, suspend the Shipping Service to the Customer until all overdue amounts are paid in full.",
  "t": "clause"
 },
 {
  "n": "4.4",
  "text": "Where the Carrier reasonably considers the Customer's credit to be unsatisfactory, it may require the Customer to provide security for payment before accepting further Bookings.",
  "t": "clause"
 },
 {
  "n": "4.5",
  "text": "The Carrier has sole discretion to apply any payment received from the Customer to any invoice or transaction, regardless of any instruction from the Customer to the contrary.",
  "t": "clause"
 },
 {
  "n": "4.6",
  "text": "Subject to clause 4.1, for Livestock, the freight rates and charges are due and payable upon completion of the Voyage in accordance with the relevant invoice, once the actual live weight of the Livestock is properly recorded at a weighing facility nominated or approved by the Carrier.",
  "t": "clause"
 },
 {
  "n": "4.7",
  "text": "Should it be considered necessary by the Carrier to incur legal or any other expenses, including any such expenses to any debt collection agency, in obtaining, or attempting to obtain, payment for any amount due by the Customer, the Customer shall be liable for all such expenses on an indemnity basis.",
  "t": "clause"
 },
 {
  "n": "4.8",
  "text": "Freight is deemed earned by the Carrier upon loading of the Goods or Livestock onto the Vessel.",
  "t": "clause"
 },
 {
  "n": "5",
  "title": "Livestock Handling and Animal Welfare",
  "t": "heading",
  "text": ""
 },
 {
  "n": "5.1",
  "text": "Livestock will be carried in accordance with the Carrier's Livestock Handling Procedures (as amended from time to time on its website).",
  "t": "clause"
 },
 {
  "n": "5.2",
  "text": "As between the Carrier and the Customer, the Owner and Consignor are responsible for ensuring Livestock are fit for the intended Voyage, correctly prepared (including off-feed and off-water periods), and accurately described in the Booking & Declaration Form. The Carrier is responsible for the proper care of Livestock, including provision of food, water, and safe carriage, from the time Livestock are accepted on board the Vessel.",
  "t": "clause"
 },
 {
  "n": "5.3",
  "text": "Where Livestock are transferred to the Vessel from a barge, truck, or other conveyance, fitness for onward transport must be assessed and documented before the animals are transferred to the Vessel. The Carrier does not accept responsibility for the pre-existing condition of any animal that was unfit for transport at the time it was accepted for carriage by any other party in the transport chain.",
  "t": "clause"
 },
 {
  "n": "5.4",
  "text": "Stocking densities, pen configuration, and loading plans on board the Vessel are determined by the Carrier in accordance with its Livestock Handling Procedures and the applicable Maritime Rules, and are not subject to variation at the direction of the Customer.",
  "t": "clause"
 },
 {
  "n": "5.5",
  "text": "The Master retains absolute discretion over the humane destruction of any Livestock during a Voyage, in accordance with the Carrier's Livestock Handling Procedures, and any decision so made will not give rise to liability on the part of the Carrier or its Personnel.",
  "t": "clause"
 },
 {
  "n": "5.6",
  "text": "Mortality, injury, and other welfare incidents will be recorded and reported in accordance with the Carrier's Livestock Handling Procedures and applicable Ministry for Primary Industries requirements.",
  "t": "clause"
 },
 {
  "n": "6",
  "title": "Dangerous Goods",
  "t": "heading",
  "text": ""
 },
 {
  "n": "6.1",
  "text": "The Customer must declare any Dangerous Goods to the Carrier at the time of Booking, including full details of classification, quantity, and packaging.",
  "t": "clause"
 },
 {
  "n": "6.2",
  "text": "Dangerous Goods must be packaged, labelled, and documented in accordance with the IMDG Code, the Land Transport Rule: Dangerous Goods 2005 (where applicable within restricted limits), and Maritime Rules Part 24A.",
  "t": "clause"
 },
 {
  "n": "6.3",
  "text": "The Carrier may refuse to accept any Dangerous Goods that are not declared or that do not comply with clause 6.2.",
  "t": "clause"
 },
 {
  "n": "6.4",
  "text": "The Customer indemnifies the Carrier for all Loss, Claims, damage, delay, fines, or expenses arising from a failure to declare Dangerous Goods, or from non-compliant packaging or documentation.",
  "t": "clause"
 },
 {
  "n": "7",
  "title": "Liability and Risk Allocation (Owner's Risk)",
  "t": "heading",
  "text": ""
 },
 {
  "n": "7.1",
  "text": "All Goods and Livestock are carried at Owner's Risk within the meaning of Part 5, Subpart 1 of the CCLA. Under a contract of carriage at Owner's Risk, the Carrier is not liable for any Loss of, or damage to, Goods or Livestock, except where that Loss or damage is intentionally caused by the Carrier.",
  "t": "clause"
 },
 {
  "n": "7.2",
  "text": "For a contract of carriage at Owner's Risk to have effect as such under the CCLA, the Customer must sign the statement set out in Schedule 1 of these Terms, including by way of electronic signature which will be deemed valid. If that statement is not signed, or if any other requirement of section 250 of the CCLA is not met, the carriage will instead take effect as a contract at limited carrier's risk, and the Carrier's liability will be limited to NZD$2,000 for each Unit of Goods lost or damaged, in accordance with the CCLA.",
  "t": "clause"
 },
 {
  "n": "7.3",
  "text": "The Carrier confirms that the freight charged under these Terms reflects a fair and reasonable differential from the freight that would be charged for the same carriage at limited carrier's risk, having regard to the difference in risk assumed by the Carrier.",
  "t": "clause"
 },
 {
  "n": "7.4",
  "text": "The Customer is strongly encouraged to hold its own insurance over Goods and Livestock carried under these Terms, given the limited nature of the Carrier's liability.",
  "t": "clause"
 },
 {
  "n": "7.5",
  "text": "The Customer acknowledges that Livestock, and any Goods that the Carrier or the Master elects to carry on deck, are carried on deck and/or as live animals at the Customer’s risk. To the fullest extent permitted by Law, the Carrier is not liable for any Loss of or damage to such Livestock or deck Goods arising from the risks inherent in deck carriage or the carriage of live animals, including exposure to weather, sea conditions, movement of the Vessel, or the condition, behaviour or welfare of the animals, except to the extent such Loss is intentionally caused by the Carrier or cannot lawfully be excluded.",
  "t": "clause"
 },
 {
  "n": "8",
  "title": "Carrier's Lien and Remedies for Non-Payment",
  "t": "heading",
  "text": ""
 },
 {
  "n": "8.1",
  "text": "The Carrier has a lien over Goods carried under these Terms in respect of unpaid freight and charges, in accordance with sections 283 to 292 of the CCLA, and may, after giving the notice required by that Act, sell any Goods held under lien by public auction to recover amounts owing.",
  "t": "clause"
 },
 {
  "n": "8.2",
  "text": "Given the animal welfare obligations associated with Livestock, the Carrier does not intend to exercise a lien over Livestock. In place of a lien, the Carrier's remedies for unpaid freight in respect of Livestock are those set out in clause 4 (suspension of the Shipping Service, interest, and security), the express right to withhold release of the Livestock from the Carrier’s possession, together with any other remedy available at Law.",
  "t": "clause"
 },
 {
  "n": "8.3",
  "text": "From the time of formation of a contract in accordance with these Terms, the Customer grants the Carrier a charge over the Goods and/or Livestock and the proceeds of:",
  "t": "clause"
 },
 {
  "label": "(a)",
  "text": "any sale of the Goods and/or Livestock, subject of the Contract to secure the payment of all amounts due and owing by the Customer under the Terms (“Secured Charge”). For the avoidance of doubt this includes, where applicable, the repayment of credit;",
  "t": "item"
 },
 {
  "label": "(b)",
  "text": "advanced by the Carrier to the Customer under these Terms;",
  "t": "item"
 },
 {
  "label": "(c)",
  "text": "the Secured Charge operates as a fixed charge over the Goods and/or Livestock, and the Customer agrees that the Secured Charge is a security interest, including as that term is defined under the PPSA.",
  "t": "item"
 },
 {
  "label": "(d)",
  "text": "neither the existence of a lien, other security interests nor any such sale shall prejudice or affect the Carrier’s right at any time to recover any such amount, or any part of any such amount, which remains at the time unpaid, from any Customer, Owner or other person liable therefore to the Carrier.",
  "t": "item"
 },
 {
  "label": "(e)",
  "text": "The Customer acknowledges and agrees that the Carrier may within its sole election register its lien and /or Secured Charge under this clause 8 on the PPSR.",
  "t": "item"
 },
 {
  "n": "8.4",
  "text": "If the Carrier requests, then the Customer must promptly do anything for the purposes of ensuring that any security interest created under, or provided for by, these Terms is enforceable, perfected (including but not limited to perfection by registration pursuant to the PPSA), maintained and is otherwise effective. This includes procuring that those things are done and any necessary consents are given by third parties to give effect to the creation and perfection of those security interests at the Customer’s sole expense.",
  "t": "clause"
 },
 {
  "n": "9",
  "title": "Customer Warranties and Responsibilities",
  "t": "heading",
  "text": ""
 },
 {
  "n": "9.1",
  "text": "The Customer warrants, represents and undertakes that all information provided in any Booking, manifest, or declaration is true, complete, and not misleading.",
  "t": "clause"
 },
 {
  "n": "9.2",
  "text": "The Customer warrants, represents and undertakes that:",
  "t": "clause"
 },
 {
  "label": "(a)",
  "text": "it is the owner or has the actual authority of the owner of the Goods or Livestock subject to the carriage;",
  "t": "item"
 },
 {
  "label": "(b)",
  "text": "the Goods are packaged for sea carriage;",
  "t": "item"
 },
 {
  "label": "(c)",
  "text": "all Livestock presented for carriage meet the fitness criteria in the Carrier's Livestock Handling Procedures; and",
  "t": "item"
 },
 {
  "label": "(d)",
  "text": "all Dangerous Goods are declared and packaged in accordance with clause 6.",
  "t": "item"
 },
 {
  "n": "9.3",
  "text": "The Customer is responsible for arranging safe and timely delivery of Goods and Livestock to the relevant Port, and for their prompt collection following discharge.",
  "t": "clause"
 },
 {
  "n": "9.4",
  "text": "The Customer indemnifies the Carrier against any Loss, Claim, cost, or claim arising from a breach of the warranties in this clause 9.",
  "t": "clause"
 },
 {
  "n": "10",
  "title": "Regulatory Compliance",
  "t": "heading",
  "text": ""
 },
 {
  "n": "10.1",
  "text": "The Customer warrants that all Goods and Livestock presented for carriage comply with all applicable Laws, regulations, permits, approvals and directions relating to biosecurity, animal welfare, food safety, customs, health and safety, dangerous goods, maritime transport and any other applicable regulatory requirements.",
  "t": "clause"
 },
 {
  "n": "10.2",
  "text": "Without limiting clause 10.1, the Customer warrants that all declarations, permits, certificates and other information required by any Applicable Authority (including the Ministry for Primary Industries) are complete, accurate and provided to the Carrier before loading.",
  "t": "clause"
 },
 {
  "n": "10.3",
  "text": "The Carrier may refuse to accept, load, carry, unload, isolate, inspect, treat, destroy, dispose of or otherwise deal with any Goods or Livestock where the Carrier reasonably considers it necessary to:",
  "t": "clause"
 },
 {
  "label": "(a)",
  "text": "comply with any applicable Laws;",
  "t": "item"
 },
 {
  "label": "(b)",
  "text": "comply with a direction or requirement of any Applicable Authority; or",
  "t": "item"
 },
 {
  "label": "(c)",
  "text": "protect the Vessel, other cargo, Livestock, persons or the environment from any actual or suspected biosecurity, contamination or regulatory risk, the Carrier will not be liable for any Loss, delay, damage or expense arising from the exercise of its rights under this clause, except to the extent caused intentionally by the Carrier.",
  "t": "item"
 },
 {
  "n": "10.4",
  "text": "The Customer indemnifies the Carrier against all Loss, liability, damage, cost, expense, penalty, fine, delay and Claim (including legal costs on a solicitor and own client basis) arising out of or in connection with:",
  "t": "clause"
 },
 {
  "label": "(a)",
  "text": "any breach of this clause by the Customer;",
  "t": "item"
 },
 {
  "label": "(b)",
  "text": "any inaccuracy or omission in any declaration, certificate or information provided by the Customer;",
  "t": "item"
 },
 {
  "label": "(c)",
  "text": "any actual or suspected biosecurity risk, contamination or disease associated with the Customer's Goods or Livestock; or",
  "t": "item"
 },
 {
  "label": "(d)",
  "text": "any seizure, detention, quarantine, treatment, destruction, disposal or regulatory action affecting the Customer's Goods or Livestock, except to the extent that the Loss, liability, damage, cost or expense is intentionally caused by the Carrier.",
  "t": "item"
 },
 {
  "n": "10.5",
  "text": "Any additional costs incurred by the Carrier as a result of compliance with this clause, including inspection, cleaning, fumigation, quarantine, storage, handling, disposal, veterinary treatment, transport or regulatory charges, are recoverable from the Customer as a debt due and payable.",
  "t": "clause"
 },
 {
  "n": "11",
  "title": "Consumer Guarantees Act 1993",
  "t": "heading",
  "text": ""
 },
 {
  "n": "11.1",
  "text": "The Customer acknowledges that Goods and Livestock are carried under these Terms for the purposes of a business, and the parties agree, in accordance with section 43(2) of the Consumer Guarantees Act 1993, that the provisions of that Act do not apply to the carriage of Goods and Livestock under these Terms.",
  "t": "clause"
 },
 {
  "n": "11.2",
  "text": "This clause takes effect only to the extent that it is fair and reasonable for the parties to be bound by it, having regard to the matters set out in section 43(2A) of the Consumer Guarantees Act 1993, including the parties' relative bargaining strength and whether the Customer received legal advice before agreeing to these Terms.",
  "t": "clause"
 },
 {
  "n": "11.3",
  "text": "The Customer warrants that it is acquiring the carriage of Goods and Livestock under these Terms in trade for the purposes of a business, and not as a consumer, and acknowledges that the Carrier has agreed to supply the carriage in reliance on that warranty and on the exclusion in this clause 11.",
  "t": "clause"
 },
 {
  "n": "11.4",
  "text": "The Customer acknowledges that:",
  "t": "clause"
 },
 {
  "label": "(a)",
  "text": "these Terms are in writing;",
  "t": "item"
 },
 {
  "label": "(b)",
  "text": "it has had the opportunity to review these Terms and to obtain independent legal advice before agreeing to them; and",
  "t": "item"
 },
 {
  "label": "(c)",
  "text": "having regard to the parties’ respective bargaining positions and the freight rates charged, it is fair and reasonable for the parties to be bound by this clause 11.",
  "t": "item"
 },
 {
  "n": "11.5",
  "text": "If, and to the extent that, the exclusion in clause 11.1 is held not to apply to any carriage under these Terms, the Carrier’s liability under the Consumer Guarantees Act 1993 is limited to the fullest extent permitted by that Act, including as provided in clause 22 (Limitation of Liability).",
  "t": "clause"
 },
 {
  "n": "12",
  "title": "Force Majeure and Schedule Variation",
  "t": "heading",
  "text": ""
 },
 {
  "n": "12.1",
  "text": "The Carrier is not liable for any failure or delay in performing its obligations under these Terms caused by weather, sea or tidal conditions, mechanical breakdown, war, civil commotion, strike or lockout, act of God, fire, or any government regulation or direction, or any other cause beyond the Carrier's reasonable control.",
  "t": "clause"
 },
 {
  "n": "12.2",
  "text": "Any sailing dates, arrival times, departure times, transit times or delivery estimates provided by the Carrier are estimates only and are not guaranteed. Time is not of the essence in relation to the performance of the Carrier's obligations under these Terms. Without limiting clause 12.1 (Force Majeure), the Carrier is not liable for any Loss, damage, expense or delay arising from:",
  "t": "clause"
 },
 {
  "label": "(a)",
  "text": "the postponement, cancellation or rescheduling of any Voyage;",
  "t": "item"
 },
 {
  "label": "(b)",
  "text": "any delay in loading, sailing, transit or discharge;",
  "t": "item"
 },
 {
  "label": "(c)",
  "text": "adverse weather, sea state, swell, tides or bar conditions;",
  "t": "item"
 },
 {
  "label": "(d)",
  "text": "Port congestion, berth availability or port closures;",
  "t": "item"
 },
 {
  "label": "(e)",
  "text": "mechanical breakdown or maintenance of the Vessel;",
  "t": "item"
 },
 {
  "label": "(f)",
  "text": "the exercise of the Master's discretion in relation to the safety of the Vessel, its crew, Goods or Livestock;",
  "t": "item"
 },
 {
  "label": "(g)",
  "text": "compliance with any law or direction of any Applicable Authority; or",
  "t": "item"
 },
 {
  "label": "(h)",
  "text": "any other event beyond the Carrier's reasonable control.",
  "t": "item"
 },
 {
  "n": "12.3",
  "text": "A delay or interruption to a Voyage does not entitle the Customer to:",
  "t": "clause"
 },
 {
  "label": "(a)",
  "text": "cancel any Booking or contract of carriage;",
  "t": "item"
 },
 {
  "label": "(b)",
  "text": "refuse or withhold payment of freight or other charges;",
  "t": "item"
 },
 {
  "label": "(c)",
  "text": "recover any Loss of profit, loss of market, business interruption, indirect loss, special loss or Consequential Loss; or",
  "t": "item"
 },
 {
  "label": "(d)",
  "text": "make any Claim against the Carrier, except to the extent liability cannot lawfully be excluded.",
  "t": "item"
 },
 {
  "n": "12.4",
  "text": "The Carrier may alter the order of loading, discharge, Ports of call, routing or scheduling, or postpone or cancel a Voyage, where it reasonably considers it necessary for operational, safety or commercial reasons, and the exercise of those rights does not constitute a breach of these Terms.",
  "t": "clause"
 },
 {
  "n": "12.5",
  "text": "The Carrier will publish an allocation policy on its website for Bookings during periods of peak demand, which will apply in place of a strict first-come-first-served allocation where the Carrier considers this necessary to manage capacity fairly across the Customer base.",
  "t": "clause"
 },
 {
  "n": "13",
  "title": "Himalaya",
  "t": "heading",
  "text": ""
 },
 {
  "n": "13.1",
  "text": "Every exemption, limitation, defence, immunity and indemnity available to the Carrier under these Terms also applies for the benefit of every Personnel of the Carrier, including without limitation every Personnel engaged by the Carrier. The Carrier enters into these Terms as trustee and agent for each such person, each of whom may rely upon and enforce this clause as if they were a party to these Terms.",
  "t": "clause"
 },
 {
  "n": "14",
  "title": "Liberty of the Carrier",
  "t": "heading",
  "text": ""
 },
 {
  "n": "14.1",
  "text": "Without limiting and in addition to clause 12 (Force Majeure and Schedule Variation), and notwithstanding any other provision of these Terms, the Carrier and the Master of the Vessel may, without liability to the Customer and without constituting a deviation, breach of contract or abandonment of the carriage:",
  "t": "clause"
 },
 {
  "label": "(a)",
  "text": "exercise any of its rights under clause 12 (Force Majeure and Schedule Variation), including to postpone, delay, interrupt or cancel any Voyage or part of a Voyage, without such exercise constituting a deviation, breach of contract or abandonment of the carriage;",
  "t": "item"
 },
 {
  "label": "(b)",
  "text": "deviate from the advertised or customary route;",
  "t": "item"
 },
 {
  "label": "(c)",
  "text": "call at, omit, or return to any Port in any order;",
  "t": "item"
 },
 {
  "label": "(d)",
  "text": "proceed with or without pilots, tugs or other assistance;",
  "t": "item"
 },
 {
  "label": "(e)",
  "text": "transfer, tranship or forward any Goods or Livestock by another vessel whether operated by the Carrier or another person or entity;",
  "t": "item"
 },
 {
  "label": "(f)",
  "text": "substitute another vessel for the Vessel;",
  "t": "item"
 },
 {
  "label": "(g)",
  "text": "load, unload, reload, stow, restow or retain Goods or Livestock on board the Vessel as the Carrier or the Master considers appropriate;",
  "t": "item"
 },
 {
  "label": "(h)",
  "text": "refuse, delay or discontinue the loading or carriage of any Goods or Livestock where the Carrier or the Master reasonably considers it necessary for the safety of the Vessel, any person, any cargo or Livestock, or to comply with any Law, direction or requirement of any Applicable Authority; and",
  "t": "item"
 },
 {
  "label": "(i)",
  "text": "take any other action which the Carrier or the Master reasonably considers necessary for the safety of the Vessel, its crew, passengers, Goods, Livestock or the marine environment.",
  "t": "item"
 },
 {
  "n": "14.2",
  "text": "Any additional costs reasonably incurred by the Carrier as a consequence of the exercise of the rights in this clause, including additional handling, storage, transport or port charges attributable to the Customer's Goods or Livestock, are payable by the Customer.",
  "t": "clause"
 },
 {
  "n": "15",
  "title": "Master’s Authority",
  "t": "heading",
  "text": ""
 },
 {
  "n": "15.1",
  "text": "The Master has absolute discretion in relation to the navigation, operation and safety of the Vessel, including all decisions concerning loading, stowage, carriage, discharge, routing, weather avoidance, animal welfare, compliance with applicable Laws, and the safety of the Vessel, crew, Goods and Livestock. The exercise of that discretion does not constitute a breach of these Terms and does not give rise to any Claim against the Carrier, except to the extent liability cannot lawfully be excluded.",
  "t": "clause"
 },
 {
  "n": "16",
  "title": "Delivery and Collection",
  "t": "heading",
  "text": ""
 },
 {
  "n": "16.1",
  "text": "Unless otherwise agreed in writing, the Carrier's responsibility for Goods and Livestock ends when they are discharged from the Vessel or otherwise made available for collection at the nominated Port.",
  "t": "clause"
 },
 {
  "n": "16.2",
  "text": "The Customer must promptly take delivery of the Goods or Livestock upon notification by the Carrier that they are available for collection. If the Customer fails to do so, the Carrier may, at the Customer's sole risk and expense:",
  "t": "clause"
 },
 {
  "label": "(a)",
  "text": "store the Goods or Livestock at the Port or any other suitable location;",
  "t": "item"
 },
 {
  "label": "(b)",
  "text": "arrange for the Goods or Livestock to be held by a third party;",
  "t": "item"
 },
 {
  "label": "(c)",
  "text": "move the Goods or Livestock to another location; or",
  "t": "item"
 },
 {
  "label": "(d)",
  "text": "take any other reasonable steps to safeguard the Goods or Livestock.",
  "t": "item"
 },
 {
  "n": "16.3",
  "text": "All costs incurred by the Carrier arising from the Customer's failure to promptly take delivery, including storage, handling, transport, agistment, feeding, veterinary, disposal and other reasonable costs, are recoverable from the Customer as a debt due and payable.",
  "t": "clause"
 },
 {
  "n": "16.4",
  "text": "If the Goods remain uncollected for more than 15 days after notification that they are available for collection, the Carrier may, after giving not less than 5 days written notice to the Customer (where reasonably practicable), sell or otherwise dispose of the Goods and apply the proceeds towards any amounts owing to the Carrier, accounting to the Customer for any surplus remaining after deduction of all reasonable costs and expenses.",
  "t": "clause"
 },
 {
  "n": "16.5",
  "text": "Where Livestock are not collected or accepted by the Customer or its Agent, the Carrier may take such steps as it reasonably considers necessary to ensure the welfare of the Livestock, including arranging agistment, veterinary treatment, return transport or delivery to another location. All costs incurred by the Carrier in doing so are payable by the Customer.",
  "t": "clause"
 },
 {
  "n": "16.6",
  "text": "Nothing in this clause limits any lien, statutory right, or other remedy available to the Carrier under these Terms or at Law.",
  "t": "clause"
 },
 {
  "n": "17",
  "title": "General Average and Salvage",
  "t": "heading",
  "text": ""
 },
 {
  "n": "17.1",
  "text": "General average shall be adjusted, stated and settled in accordance with the York-Antwerp Rules 2016 (or any subsequent revision or replacement of those Rules), except to the extent inconsistent with applicable Laws.",
  "t": "clause"
 },
 {
  "n": "17.2",
  "text": "The Customer must contribute, in proportion to the value of its Goods or Livestock, to any general average sacrifice, expenditure or loss properly incurred for the common safety of the Vessel, the Goods, the Livestock and all other property involved in the Voyage.",
  "t": "clause"
 },
 {
  "n": "17.3",
  "text": "The Customer is liable for its proportion of any salvage award, salvage remuneration, special compensation, or other maritime charges lawfully incurred in preserving, recovering or protecting the Vessel, the Goods, the Livestock or any other property carried on the Voyage.",
  "t": "clause"
 },
 {
  "n": "17.4",
  "text": "The Carrier may require the Customer to provide such cash deposit, guarantee, bond or other security as the Carrier reasonably requires in respect of any actual or anticipated contribution to general average, salvage or associated expenses before releasing the Goods or Livestock.",
  "t": "clause"
 },
 {
  "n": "17.5",
  "text": "The Carrier is not obliged to deliver or release the Goods or Livestock until the Customer has complied with clause 17.4.",
  "t": "clause"
 },
 {
  "n": "17.6",
  "text": "The Customer must reimburse the Carrier for all reasonable costs and expenses incurred by the Carrier in connection with the adjustment, collection or recovery of any general average or salvage contribution payable by the Customer.",
  "t": "clause"
 },
 {
  "n": "17.7",
  "text": "Nothing in this clause limits or affects any maritime lien, statutory lien, possessory lien or other right or remedy available to the Carrier at Law.",
  "t": "clause"
 },
 {
  "n": "18",
  "title": "Both-to-Blame Collision",
  "t": "heading",
  "text": ""
 },
 {
  "n": "18.1",
  "text": "If the Vessel comes into collision with another vessel as a result of the negligence of the other vessel and any act, neglect or default of the Master, crew, pilot or other Personnel of the Carrier in the navigation or management of the Vessel, the Customer will indemnify the Carrier against all Loss or liability to the other or non-carrying vessel or her owners in so far as such Loss or liability represents Loss of, or damage to, or any Claim of the Customer paid or payable by the other or non-carrying vessel or her owners to the Customer and set off, recouped or recovered by the other or non-carrying vessel or her owners as part of their claim against the carrying Vessel or the Carrier. This provision also applies where the owners, operators or those in charge of any vessel or vessels or objects other than, or in addition to, the colliding vessels or objects are at fault in respect of a collision or contact.",
  "t": "clause"
 },
 {
  "n": "19",
  "title": "New Jason Clause",
  "t": "heading",
  "text": ""
 },
 {
  "n": "19.1",
  "text": "In the event of accident, danger, damage or disaster before or after commencement of the Voyage resulting from any cause whatsoever, whether due to negligence or not, for which, or for the consequence of which, the Carrier is not responsible by statute, contract or otherwise, the Goods, Livestock and the Customer will jointly and severally contribute with the Carrier in general average to the payment of any sacrifices, losses or expenses of a general average nature that may be made or incurred, and will pay salvage and special charges incurred in respect of the Goods and Livestock. If a salving vessel is owned or operated by the Carrier, salvage will be paid for as fully as if that salving vessel belonged to a third party.",
  "t": "clause"
 },
 {
  "n": "20",
  "title": "Claims Process",
  "t": "heading",
  "text": ""
 },
 {
  "n": "20.1",
  "text": "Any Claim for Loss of, or damage to, Goods or Livestock must be notified to the Carrier in writing, with reasonable particulars, within 30 days of the Carrier's responsibility for the Goods or Livestock ending.",
  "t": "clause"
 },
 {
  "n": "20.2",
  "text": "The Carrier will not be liable for any Claim not notified within the period in clause 20.1, or for any Loss that is consequential, indirect, or economic in nature, except as set out in clause 7.",
  "t": "clause"
 },
 {
  "n": "20.3",
  "text": "In any event, and notwithstanding clause 20.1, the Carrier is discharged from all liability whatsoever in respect of the Goods or Livestock unless suit is brought, and written notice of the Claim is given to the Carrier, within 12 months after the date the Goods or Livestock were delivered or should have been delivered, except to the extent this limitation cannot lawfully be excluded.",
  "t": "clause"
 },
 {
  "n": "21",
  "title": "Termination",
  "t": "heading",
  "text": ""
 },
 {
  "n": "21.1",
  "text": "The Carrier may terminate any Booking or contract of carriage with immediate effect, without liability, if the Customer becomes insolvent, enters into any composition or arrangement with creditors, or (if a company) becomes subject to administration, liquidation or receivership.",
  "t": "clause"
 },
 {
  "n": "21.2",
  "text": "Termination does not entitle the Customer to a refund of any amount already paid, and does not relieve the Customer of liability for any amount owing, or for any breach of these Terms occurring before termination.",
  "t": "clause"
 },
 {
  "n": "22",
  "title": "Limitation of Liability",
  "t": "heading",
  "text": ""
 },
 {
  "n": "22.1",
  "text": "To the fullest extent permitted by law, and subject to the CCLA, the total aggregate liability of the Carrier arising out of or in connection with these Terms and each contract of carriage, whether in contract, tort (including negligence), equity, under statute or otherwise, must not exceed:",
  "t": "clause"
 },
 {
  "label": "(a)",
  "text": "where the carriage is at owner's risk, the liability (if any) imposed on the Carrier under the CCLA; or",
  "t": "item"
 },
 {
  "label": "(b)",
  "text": "where the carriage is at limited carrier's risk, the maximum liability of the Carrier under the CCLA.",
  "t": "item"
 },
 {
  "n": "22.2",
  "text": "The limitation in this clause applies regardless of the number or nature of:",
  "t": "clause"
 },
 {
  "label": "(a)",
  "text": "Claims made;",
  "t": "item"
 },
 {
  "label": "(b)",
  "text": "causes of action;",
  "t": "item"
 },
 {
  "label": "(c)",
  "text": "claimants; or",
  "t": "item"
 },
 {
  "label": "(d)",
  "text": "legal proceedings arising out of the same Booking, Voyage or occurrence.",
  "t": "item"
 },
 {
  "n": "22.3",
  "text": "The Customer acknowledges that the freight charged by the Carrier has been determined on the basis of the exclusions and limitations of liability contained in these Terms and that the Customer has had the opportunity to obtain insurance in respect of the Goods and Livestock.",
  "t": "clause"
 },
 {
  "n": "22.4",
  "text": "For the purposes of this clause 22, the “aggregate liability” of the Carrier means the total, combined maximum amount for which the Carrier may be liable for all Claims, whether arising from a single event or a series of related events. The cap in clause 22.1 applies once only to all Claims arising out of or in connection with the same Booking, Voyage or occurrence, and not separately to each Claim; and where the carriage is at limited carrier’s risk, that cap is calculated in accordance with the per Unit of Goods limit under the CCLA.",
  "t": "clause"
 },
 {
  "n": "22.5",
  "text": "To the fullest extent permitted by law, and without limiting clause 22.1, the Carrier is not liable for any Consequential Loss arising out of or in connection with these Terms or any carriage, whether in contract, tort (including negligence), equity, under statute or otherwise, and whether or not the Carrier was advised of the possibility of such Loss, except to the extent such liability cannot lawfully be excluded.",
  "t": "clause"
 },
 {
  "n": "22.6",
  "text": "Each exclusion and limitation of liability in this clause 22 also applies for the benefit of the Carrier’s Personnel in accordance with clause 13 (Himalaya).",
  "t": "clause"
 },
 {
  "n": "23",
  "title": "Dispute Resolution",
  "t": "heading",
  "text": ""
 },
 {
  "n": "23.1",
  "text": "If a dispute arises out of or in connection with these Terms (a “Dispute”), a party may give the other written notice of the Dispute setting out reasonable particulars (a “Dispute Notice”). Except as permitted by clause 23.6, neither party may commence court proceedings in respect of a Dispute unless it has first complied with this clause 23.",
  "t": "clause"
 },
 {
  "n": "23.2",
  "text": "Within 10 Business Days after a Dispute Notice is given, senior representatives of each party with authority to settle the Dispute must meet (in person or by video conference) and negotiate in good faith to resolve the Dispute.",
  "t": "clause"
 },
 {
  "n": "23.3",
  "text": "If the Dispute is not resolved within 10 Business Days after the meeting under clause 23.2 (or within 20 Business Days after the Dispute Notice if no meeting occurs), the Dispute must be referred to mediation administered by the Arbitrators’ and Mediators’ Institute of New Zealand (“AMINZ”). The mediator will be agreed between the parties or, failing agreement within 5 Business Days, appointed on the application of either party by the President of AMINZ. The mediation must commence within 20 Business Days after referral, and the parties share the mediator’s fees equally.",
  "t": "clause"
 },
 {
  "n": "23.4",
  "text": "If the Dispute is not resolved within 20 Business Days after the commencement of mediation, it must be referred to and finally resolved by arbitration under the Arbitration Act 1996 before a single arbitrator. The arbitrator will be agreed between the parties or, failing agreement within 5 Business Days, appointed on the application of either party by the President of AMINZ. The seat of arbitration is Auckland, New Zealand, the language is English, and the arbitration (including its existence, all submissions and any award) is confidential.",
  "t": "clause"
 },
 {
  "n": "23.5",
  "text": "The arbitrator has power to award the Carrier its costs (including legal costs on a solicitor and own client basis), interest and any other relief. Nothing in this clause 23 extends or affects the time bar in clause 20.3 or any applicable limitation period.",
  "t": "clause"
 },
 {
  "n": "23.6",
  "text": "Nothing in this clause 23 prevents the Carrier from at any time, and without first complying with clauses 23.1 to 23.4:",
  "t": "clause"
 },
 {
  "label": "(a)",
  "text": "commencing court proceedings to recover any freight, charges or other amount due and payable by the Customer, which the Customer agrees is a liquidated debt not subject to mediation or arbitration at the Carrier’s election; or",
  "t": "item"
 },
 {
  "label": "(b)",
  "text": "seeking urgent interlocutory, injunctive or interim relief, or exercising or enforcing any lien, charge or security interest under clause 8, from any court of competent jurisdiction.",
  "t": "item"
 },
 {
  "n": "23.7",
  "text": "Each party must continue to perform its obligations under these Terms, and the Customer must continue to pay all amounts that are not genuinely and reasonably in dispute, pending resolution of any Dispute.",
  "t": "clause"
 },
 {
  "n": "24",
  "title": "No Waiver",
  "t": "heading",
  "text": ""
 },
 {
  "n": "24.1",
  "text": "No failure, delay or omission by the Carrier in exercising any right, power or remedy under these Terms or at law operates as a waiver of that right, power or remedy, nor does any single or partial exercise of any right, power or remedy prevent any further or other exercise of that or any other right, power or remedy.",
  "t": "clause"
 },
 {
  "n": "24.2",
  "text": "A waiver by the Carrier is effective only if it is in writing and signed by an authorised representative of the Carrier. A waiver applies only to the specific matter in respect of which it is given and does not constitute a continuing waiver or a waiver of any subsequent breach.",
  "t": "clause"
 },
 {
  "n": "25",
  "title": "Severability",
  "t": "heading",
  "text": ""
 },
 {
  "n": "25.1",
  "text": "If any provision of these Terms is held to be illegal, invalid, void, unenforceable or in conflict with any applicable law, that provision will, to the extent necessary, be severed from these Terms without affecting the legality, validity or enforceability of the remaining provisions, which will continue in full force and effect.",
  "t": "clause"
 },
 {
  "n": "25.2",
  "text": "Where any provision is capable of operating in part, it will continue to operate to the maximum extent permitted by Law.",
  "t": "clause"
 },
 {
  "n": "26",
  "title": "Assignment",
  "t": "heading",
  "text": ""
 },
 {
  "n": "26.1",
  "text": "The Customer must not assign, novate, transfer, subcontract or otherwise dispose of any of its rights or obligations under these Terms or any Booking without the Carrier's prior written consent.",
  "t": "clause"
 },
 {
  "n": "26.2",
  "text": "The Carrier may assign, novate, subcontract or otherwise transfer any of its rights or obligations under these Terms to any related company, contractor, subcontractor or other carrier without the Customer's consent, provided that such assignment, novation, subcontracting or transfer does not relieve the Carrier of any liability which cannot lawfully be excluded.",
  "t": "clause"
 },
 {
  "n": "27",
  "title": "Entire Agreement",
  "t": "heading",
  "text": ""
 },
 {
  "n": "27.1",
  "text": "These Terms constitute the entire agreement between the Carrier and the Customer in relation to the carriage of Goods and Livestock and supersede all prior negotiations, representations, understandings and agreements relating to that subject matter.",
  "t": "clause"
 },
 {
  "n": "27.2",
  "text": "The Customer acknowledges that it has not relied upon any representation, warranty or statement made by or on behalf of the Carrier other than those expressly set out in these Terms.",
  "t": "clause"
 },
 {
  "n": "27.3",
  "text": "No variation to these Terms is effective unless made in writing and approved by the Carrier.",
  "t": "clause"
 },
 {
  "n": "28",
  "title": "Privacy",
  "t": "heading",
  "text": ""
 },
 {
  "n": "28.1",
  "text": "Each party must comply with the Privacy Act 2020 in relation to any personal information collected, held, used or disclosed in connection with these Terms. We may collect, use and disclose personal information provided by or about you for purposes connected with the supply, purchase and administration of livestock, including verifying identity and ownership, processing payments, maintaining records, complying with legal and regulatory requirements, and managing our relationship with you. We will handle that personal information in accordance with the Privacy Act 2020 and the applicable information privacy principles.",
  "t": "clause"
 },
 {
  "n": "29",
  "title": "Governing Law",
  "t": "heading",
  "text": ""
 },
 {
  "n": "29.1",
  "text": "These Terms are governed by the laws of New Zealand. Subject to the dispute resolution provisions in clause 23 (including the agreement to arbitrate), the parties submit to the exclusive jurisdiction of the courts of Auckland, New Zealand for any matter not required to be referred to arbitration, including any application in support of or supervision of the arbitration.",
  "t": "clause"
 }
];

const OWNERS_RISK_STATEMENT =
  "“These goods are to be carried at owner’s risk. This means that the carrier will not pay compensation if the goods are lost or damaged unless he intentionally loses or damages them.”";

function renderDef(text) {
  // Bold the defined term at the start of a definition, e.g. “Agent” means ...
  const m = text.match(/^([“"][^”"]+[”"])(.*)$/s);
  if (!m) return text;
  return (<><strong className="font-semibold text-slate-900">{m[1]}</strong>{m[2]}</>);
}

export default function LivestockTerms() {
  const [active, setActive] = useState("");

  const headings = useMemo(() => TERMS.filter((b) => b.t === "heading"), []);

  useEffect(() => {
    window.scrollTo(0, 0);
    const els = headings.map((h) => document.getElementById(`clause-${h.n}`)).filter(Boolean);
    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActive(visible[0].target.id);
      },
      { rootMargin: "-80px 0px -70% 0px" }
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, [headings]);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-700 print:bg-white">
      <style>{`@media print { .no-print { display: none !important; } .print-card { box-shadow: none !important; border: none !important; } }`}</style>

      <header className="no-print border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4 sm:px-6">
          <Link to="/apply" className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-slate-900">
            <ArrowLeft size={16} /> Back to account application
          </Link>
          <button type="button" onClick={() => window.print()}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
            <Printer size={16} /> Print / save as PDF
          </button>
        </div>
      </header>

      <div className="mx-auto grid max-w-6xl gap-6 px-4 py-8 sm:px-6 lg:grid-cols-[260px_1fr]">
        <aside className="no-print hidden lg:block">
          <nav className="sticky top-6 max-h-[calc(100vh-3rem)] overflow-y-auto rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
            <p className="mb-2 px-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Contents</p>
            <ul className="space-y-0.5">
              {headings.map((h) => (
                <li key={h.n}>
                  <a href={`#clause-${h.n}`}
                    className={`block rounded-lg px-2 py-1.5 text-sm ${active === `clause-${h.n}` ? "bg-slate-900 font-semibold text-white" : "text-slate-600 hover:bg-slate-100"}`}>
                    {h.n}. {h.title}
                  </a>
                </li>
              ))}
              <li>
                <a href="#schedule-1"
                  className={`block rounded-lg px-2 py-1.5 text-sm ${active === "schedule-1" ? "bg-slate-900 font-semibold text-white" : "text-slate-600 hover:bg-slate-100"}`}>
                  Schedule 1 — Owner’s Risk Statement
                </a>
              </li>
            </ul>
          </nav>
        </aside>

        <main className="print-card rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-10">
          <div className="border-b border-slate-200 pb-6">
            <p className="text-sm font-semibold uppercase tracking-wide text-slate-500">McCallum Bros Limited</p>
            <h1 className="mt-2 text-3xl font-bold tracking-tight text-slate-900">Terms of Trade for Carriage of Goods and Livestock</h1>
            <p className="mt-2 text-slate-600">Chatham and Pitt Islands – Mainland New Zealand Shipping Service</p>
            <p className="mt-1 text-sm text-slate-500">MV William Fraser · Dated {TERMS_DATE} · Version {TERMS_VERSION}</p>
          </div>

          <div className="mt-6 space-y-3 text-[15px] leading-relaxed">
            <h2 className="text-lg font-bold uppercase tracking-wide text-slate-900">Operative Provisions</h2>
            {TERMS.filter((b) => b.t === "intro").map((b, i) => <p key={i}>{b.text}</p>)}

            {TERMS.filter((b) => b.t !== "intro").map((b, i) => {
              switch (b.t) {
                case "heading":
                  return (
                    <h3 key={i} id={`clause-${b.n}`} className="scroll-mt-6 pt-6 text-lg font-bold text-slate-900">
                      {b.n}. {b.title}
                    </h3>
                  );
                case "clause":
                  return (
                    <p key={i} className="flex gap-3">
                      <span className="w-12 shrink-0 font-semibold text-slate-500">{b.n}</span>
                      <span>{b.text}</span>
                    </p>
                  );
                case "item":
                  return (
                    <p key={i} className="flex gap-3 pl-[3.75rem]">
                      <span className="w-8 shrink-0 text-slate-500">{b.label}</span>
                      <span>{b.text}</span>
                    </p>
                  );
                case "def":
                  return <p key={i} className="pl-[3.75rem]">{renderDef(b.text)}</p>;
                case "defitem":
                  return (
                    <p key={i} className="flex gap-3 pl-[6.25rem]">
                      <span className="w-6 shrink-0 text-slate-500">{b.label}</span>
                      <span>{b.text}</span>
                    </p>
                  );
                case "deftrailer":
                  return <p key={i} className="pl-[6.25rem]">{b.text}</p>;
                default:
                  return null;
              }
            })}
          </div>

          <section id="schedule-1" className="mt-10 scroll-mt-6 border-t border-slate-200 pt-8">
            <h2 className="text-xl font-bold text-slate-900">Schedule 1 — Owner’s Risk Statement</h2>
            <p className="mt-3 text-[15px] leading-relaxed">
              In accordance with section 250 of the Contract and Commercial Law Act 2017, the following statement must be signed by the Customer for carriage under these Terms to take effect at Owner’s Risk:
            </p>
            <blockquote className="my-4 rounded-2xl border border-slate-200 bg-slate-50 p-5 text-[15px] font-medium leading-relaxed text-slate-900">
              {OWNERS_RISK_STATEMENT}
            </blockquote>
            <p className="text-[15px] leading-relaxed">
              The Customer further acknowledges and agrees that it is entering into these Terms in trade for the purposes of a business and not as a consumer, that it has had the opportunity to obtain independent legal advice, and that, in accordance with section 43(2) of the Consumer Guarantees Act 1993, the provisions of that Act do not apply to the carriage of Goods and Livestock under these Terms (see clause 11).
            </p>
            <p className="no-print mt-4 rounded-2xl border border-blue-100 bg-blue-50 p-4 text-sm text-blue-900">
              When you submit the account application you sign electronically; clause 7.2 provides that an electronic signature is deemed valid for the purposes of this Schedule.
            </p>
          </section>

          <p className="mt-10 text-center text-xs text-slate-400">
            McCallum Bros Limited (NZBN 9429040749303) · Terms of Trade for MV William Fraser · Dated {TERMS_DATE}
          </p>
        </main>
      </div>
    </div>
  );
}
